import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gavel, ShieldCheck, FileCheck, Truck, MapPin, ArrowRight, Layers, Percent, Boxes } from 'lucide-react';
import { getCurrentAuction, listVehicles } from '@/api/auction';
import { Countdown } from '@/components/auction/Countdown';
import { LotCard } from '@/components/auction/LotCard';
import { BuyerAuthDialog } from '@/components/auction/BuyerAuthDialog';

const HERO_BG = '/generated/hero-usedcars.jpg';

const WHY = [
  { icon: ShieldCheck, t: 'Fleet-maintained', d: 'Every vehicle comes from our managed fleet — serviced on schedule in our A-grade workshops.' },
  { icon: FileCheck, t: 'Full documentation', d: 'Verified ownership, service history and clear VAT treatment on every lot.' },
  { icon: Layers, t: 'Genuine variety', d: 'Ex-rental, lease returns, fleet disposals and trade-ins across sedans, SUVs and commercials.' },
  { icon: Truck, t: 'Volume-ready', d: 'Bulk buying for dealers and exporters — one yard, one relationship, weekly stock.' },
];

const SOURCES = ['Ex-Rental', 'Lease Return', 'Fleet Disposal', 'Trade-In', 'Repossession', 'Damaged Vehicle'];
const STEPS = [
  { n: '01', t: 'Browse the lots', d: 'View the full catalogue of vehicles listed for the current weekly auction.' },
  { n: '02', t: 'Register to bid', d: 'Create a free B2B buyer account in under a minute — auto-approved, no waiting.' },
  { n: '03', t: 'Submit sealed offers', d: 'Place your best offer per vehicle before the auction closes. Reserves stay confidential.' },
  { n: '04', t: 'Win & collect', d: 'Our team reviews offers, confirms the winners and arranges documentation and release.' },
];

export default function UsedCarsPage() {
  const [current, setCurrent] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    document.title = 'Eurogulf Used Cars & Weekly B2B Auction | Ex-Fleet Vehicles Dubai';
    getCurrentAuction().then(setCurrent).catch(() => {});
    listVehicles({ featured: true, limit: 8 }).then((d) => {
      if (d.vehicles.length) setFeatured(d.vehicles);
      else listVehicles({ limit: 8 }).then((x) => setFeatured(x.vehicles)).catch(() => {});
    }).catch(() => {});
  }, []);

  const auction = current?.auction;

  return (
    <div data-testid="used-cars-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#121212] text-white">
        <img src={HERO_BG} alt="Eurogulf Used Cars showroom in Dubai" className="absolute inset-0 h-full w-full object-cover object-[center_45%] opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28">
          <img src="/brands/used-cars-white.webp" alt="Eurogulf Used Cars" className="h-10 sm:h-11 w-auto mb-5" />
          <span className="chip-orange">Eurogulf Mobility · Used Cars</span>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight max-w-3xl">The weekly B2B auction for ex-fleet vehicles.</h1>
          <p className="mt-5 font-body text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">Dealers and exporters buy verified, fleet-maintained vehicles direct from Eurogulf Mobility Group — every week, by sealed offer.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link to="/auction" data-testid="hero-browse-auction" className="btn-primary">Browse the auction <ArrowRight className="w-4 h-4" /></Link>
            <button type="button" data-testid="hero-register" onClick={() => setAuthOpen(true)} className="btn-outline !border-white !text-white hover:!bg-white hover:!text-[#121212]">Register here to bid</button>
          </div>
        </div>
      </section>

      {/* AUCTION COUNTDOWN BAR */}
      <section className="bg-[#1A1A1C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 text-[#EE5A01]"><Gavel className="w-4 h-4" /><span className="font-heading font-bold text-[11px] uppercase tracking-[0.2em]">{auction ? 'Next auction closes in' : 'Next auction'}</span></div>
            {auction ? (
              <>
                <h2 className="mt-2 font-heading font-black text-xl sm:text-2xl">{auction.title}</h2>
                <p className="mt-1 font-body text-sm text-white/60 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span>{new Date(auction.starts_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                  {auction.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{auction.location}</span>}
                  <span>{current?.lot_count || 0} lots</span>
                </p>
              </>
            ) : (
              <h2 className="mt-2 font-heading font-black text-xl sm:text-2xl">New auction dates announced weekly</h2>
            )}
          </div>
          <div className="flex items-center gap-6">
            {auction && <Countdown target={auction.ends_at} />}
            <Link to="/auction" className="btn-primary hidden sm:inline-flex">View lots</Link>
          </div>
        </div>
      </section>

      {/* FEATURED LOTS */}
      <section className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <span className="chip-orange">Featured Lots</span>
              <h2 className="mt-3 font-heading font-bold text-2xl sm:text-3xl text-[#121212] tracking-tight">This week's highlights</h2>
            </div>
            <Link to="/auction" className="font-heading font-bold text-sm text-[#EE5A01] hover:underline hidden sm:inline">View all lots →</Link>
          </div>
          {featured.length ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((v) => <LotCard key={v.id} v={v} />)}
            </div>
          ) : (
            <div className="mt-8 rounded-2xl border border-dashed border-black/15 bg-[#FAFAFA] p-12 text-center">
              <Boxes className="w-10 h-10 text-[#C4C4BE] mx-auto" />
              <p className="mt-4 font-heading font-bold text-[#121212]">Fresh stock is listed every week.</p>
              <p className="mt-1 font-body text-sm text-[#666666]">Register now and we'll notify you the moment the next auction opens.</p>
              <button type="button" onClick={() => setAuthOpen(true)} className="btn-primary mt-6">Register to bid</button>
            </div>
          )}
        </div>
      </section>

      {/* VEHICLE SOURCES + VAT */}
      <section className="bg-[#FAFAFA] border-y border-black/5 py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <span className="chip-orange">Vehicle Sources</span>
            <h2 className="mt-3 font-heading font-bold text-2xl sm:text-3xl text-[#121212] tracking-tight">Know exactly what you're buying.</h2>
            <p className="mt-3 font-body text-base text-[#666666]">Every lot is clearly tagged by source and VAT treatment, so you can bid with total confidence.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {SOURCES.map((s) => <span key={s} className="font-body text-sm text-[#333] bg-white border border-black/10 px-3 py-1.5 rounded-full">{s}</span>)}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {WHY.map((w) => (
              <div key={w.t} className="bg-white p-5 transition-colors hover:bg-[#FFF4ED]/70">
                <w.icon className="w-5 h-5 text-[#EE5A01]" />
                <h3 className="mt-4 font-heading font-bold text-sm text-[#121212]">{w.t}</h3>
                <p className="mt-1 font-body text-xs text-[#666666] leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="chip-orange">How the Auction Works</span>
            <h2 className="mt-3 font-heading font-bold text-2xl sm:text-3xl text-[#121212] tracking-tight">Sealed offers, simple process.</h2>
            <p className="mt-3 font-body text-base text-[#666666] inline-flex items-center gap-2 justify-center"><Percent className="w-4 h-4 text-[#EE5A01]" /> Pricing and reserves are confidential — you submit your best offer.</p>
          </div>
          <ol className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-xl border border-black/[0.06] bg-black/[0.06]">
            {STEPS.map((s) => (
              <li key={s.n} className="bg-white p-6 transition-colors hover:bg-[#FFF4ED]/70">
                <span className="font-heading font-black text-2xl text-[#EE5A01] leading-none">{s.n}</span>
                <h3 className="mt-5 font-heading font-bold text-base text-[#121212]">{s.t}</h3>
                <p className="mt-2 font-body text-sm text-[#666666] leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
          <div className="mt-10 text-center">
            <Link to="/auction" className="btn-primary">Browse the auction <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <BuyerAuthDialog open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" />
    </div>
  );
}
